/**
 * Lector de CSV para la importación de productos. Función pura.
 *
 * Excel en español exporta con `;` y coma decimal; Sheets y la mayoría de
 * sistemas con `,`; algunos con tabulador. Se detecta el separador contando
 * cuál aparece más veces fuera de comillas en las primeras líneas, y se
 * respetan los campos entrecomillados (con comas, saltos de línea y `""`).
 */

const CANDIDATES = [',', ';', '\t', '|'];

function stripBom(text: string): string {
    return text.charCodeAt(0) === 0xFEFF ? text.slice(1) : text;
}

export function detectDelimiter(text: string): string {
    const sample = text.split(/\r?\n/).slice(0, 20).join('\n');
    let best = ',';
    let bestCount = -1;
    CANDIDATES.forEach(delimiter => {
        let count = 0;
        let quoted = false;
        for (let i = 0; i < sample.length; i++) {
            const ch = sample.charAt(i);
            if (ch === '"') quoted = !quoted;
            else if (!quoted && ch === delimiter) count++;
        }
        if (count > bestCount) {
            best = delimiter;
            bestCount = count;
        }
    });
    return best;
}

/** Filas × columnas, sin las filas totalmente vacías. */
export function parseCsv(rawText: string, delimiter?: string): string[][] {
    const text = stripBom(String(rawText || ''));
    const sep = delimiter || detectDelimiter(text);
    const rows: string[][] = [];
    let row: string[] = [];
    let field = '';
    let quoted = false;

    for (let i = 0; i < text.length; i++) {
        const ch = text.charAt(i);

        if (quoted) {
            if (ch === '"') {
                if (text.charAt(i + 1) === '"') {
                    field += '"';
                    i++;
                } else {
                    quoted = false;
                }
            } else {
                field += ch;
            }
            continue;
        }

        if (ch === '"') {
            quoted = true;
        } else if (ch === sep) {
            row.push(field);
            field = '';
        } else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && text.charAt(i + 1) === '\n') i++;
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        } else {
            field += ch;
        }
    }
    if (field !== '' || row.length > 0) {
        row.push(field);
        rows.push(row);
    }

    return rows
        .map(r => r.map(cell => String(cell).trim()))
        .filter(r => r.some(cell => cell !== ''));
}

/**
 * Los archivos guardados por Excel en Windows suelen venir en Latin-1, no en
 * UTF-8; si la decodificación UTF-8 deja caracteres de reemplazo se reintenta
 * en windows-1252 para no perder las tildes y las eñes.
 */
export function decodeCsvBytes(bytes: ArrayBuffer): string {
    const utf8 = new TextDecoder('utf-8').decode(bytes);
    if (utf8.indexOf('�') < 0) return utf8;
    try {
        return new TextDecoder('windows-1252').decode(bytes);
    } catch (e) {
        return utf8;
    }
}
